import React, { useEffect, useRef, useState } from 'react'

import getViewProfiles from '@/services/ProductService/getViewProfiles'
import deleteViewProfile from '@/services/ProductService/deleteViewProfile'
import Modal from '@/components/Modal/Modal'
import ViewProfileForm from '@/components/ViewProfile/ViewProfileForm'

import { closeNotif, startNotif } from '@/components/DynamicForm/util/notifier'

import SearchBar from '@/components/UiElements/SearchBar'
import Splash from '@/components/splash/Splash'
import Pagination from '@/components/UiElements/Pagination'
import ViewProfileItems from '@/components/ViewProfile/ViewProfileItems'

const perPage = 20

export default function ViewProfilePage() {
  const paginationRef = useRef<any>(null)
  const [profiles, setProfiles] = useState<Array<any>>([])
  const [filteredProfiles, setFilteredProfiles] = useState<Array<any>>([])
  const filteredProfilesRef = useRef<Array<any>>([])
  const [total, setTotal] = React.useState<number>(0)
  const [loading, setLoading] = React.useState<boolean>(true)

  const [selectedProfile, setSelectedProfile] = useState<any>(null)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const [filter, setFilter] = useState<{ text: string }>({
    text: '',
  })

  const handleGetPageData = (pageNumber: number) => {
    const start = (pageNumber - 1) * perPage
    const end = start + perPage
    return filteredProfilesRef.current.slice(start, end)
  }

  const handleGetProfileList = async () => {
    const list: any[] = (await getViewProfiles(1)) || []
    setTotal(Math.ceil(list.length / perPage))
    setProfiles(list)
    setLoading(false)
  }

  const handleOpenModal = (item: any) => {
    setIsModalOpen(true)
    setSelectedProfile(item)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedProfile(null)
  }

  const handleOnUpdateSuccess = () => {
    handleCloseModal()
    handleGetProfileList()
  }

  const handleAddNewProfile = () => {
    setIsModalOpen(true)
    setSelectedProfile({})
  }

  const handleDeleteModel = async (id: string) => {
    const toast = startNotif('Deleting view profile...')
    const delRes = await deleteViewProfile(id)
    if (delRes !== null) {
      closeNotif(toast, 'View profile deleted successfully.')
      handleGetProfileList()
    } else {
      closeNotif(toast, 'Failed to delete view profile.', { type: 'error' })
    }
  }

  useEffect(() => {
    handleGetProfileList()
  }, [])

  useEffect(() => {
    const newList = profiles.filter((profile) => {
      const string = `${(profile.name || '').toLowerCase()}`
      const text = filter.text.toLowerCase()
      return string.includes(text)
    })

    filteredProfilesRef.current = newList
    setFilteredProfiles(newList)
    paginationRef.current?.reset()
    setTotal(Math.ceil(newList.length / perPage))
  }, [filter, profiles])

  return (
    <div className={'min-h-[100%] relative'}>
      <Modal
        isOpen={isModalOpen}
        onClose={() => handleCloseModal()}
        title={
          selectedProfile
            ? selectedProfile.id === 'duplicate'
              ? 'Duplicate view profile'
              : selectedProfile.id
                ? 'Edit view profile'
                : 'Add view profile'
            : 'Add view profile'
        }
        closeOnOutsideClick={false}
      >
        <ViewProfileForm
          profile={selectedProfile}
          onUpdateSuccess={handleOnUpdateSuccess}
          onCancel={handleCloseModal}
        />
      </Modal>
      <div className={'sticky top-[48px] bg-white z-[10]'}>
        <div className={'h-[48px] absolute top-[-48px] w-full bg-white z-[-1]'}></div>
        <SearchBar
          placeholder={'Search by view profile name'}
          searchText={filter.text}
          onChange={(text) => setFilter({ text })}
          options={[
            {
              label: (
                <span className="flex flex-row gap-md">
                  <span className="font-icon-solid text-primary">+</span>
                  New Profile
                </span>
              ),
              onClick: handleAddNewProfile,
            },
          ]}
        />
      </div>
      {loading && <Splash />}
      {filteredProfiles.length > 0 && total > 0 ? (
        <Pagination
          ref={paginationRef}
          theme={(data) => {
            return <ViewProfileItems profile={data} callBack={handleOpenModal} onDelete={handleDeleteModel} />
          }}
          total={total}
          offset={150}
          getPageData={handleGetPageData}
          className={'pt-xxl'}
        />
      ) : (
        <div
          className={
            'flex flex-row justify-center items-center px-mx py-xl text-gray-300 bg-gray-50 rounded-md mt-xxl gap-lg h-[150px]'
          }
        >
          <p>
            <span>No results match your current filters. Try </span>
            <span
              className={'text-gray-300 underline cursor-pointer hover:text-gray-900 transition-all'}
              onClick={() => setFilter({ text: '' })}
            >
              resetting some filters
            </span>
            <span> to see more options.</span>
          </p>
        </div>
      )}
    </div>
  )
}
